import { useContext, useEffect, useMemo, useState } from 'react';

import {
  Cancel,
  CheckCircle,
  Circle,
  KeyboardArrowDown
} from '@mui/icons-material';
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';

import Alert from '@/components/alert';
import Button from '@/components/button';
import Card from '@/components/card';
import OpenIcon from '@/components/icons/status/OpenIcon';
import OtherIcon from '@/components/icons/status/OtherIcon';
import { ViolationContext } from '@/contexts/ViolationContext';
import { useUpdateViolationStatus } from '@/hooks/queries/violations';
import { useAppSelector } from '@/hooks/redux';
import useAlert from '@/hooks/useAlert';
import { Violations } from '@/models/violations';
import { getPrettyDateWithTime } from '@/utils/datesAndTime/datesAndTime';

const selectColor: Record<
  Violations.StatusStage,
  'success' | 'warning' | 'info' | 'primary'
> = {
  open: 'warning',
  closed: 'success',
  other: 'primary',
  '': 'primary'
};

const statusOption = [
  { value: 'open', label: 'Open', color: 'warning' },
  { value: 'closed', label: 'Closed', color: 'success' },
  { value: 'other', label: 'Other', color: 'disabled' }
];

const alertIcon: Record<Violations.StatusStage, React.JSX.Element> = {
  other: <OtherIcon fontSize="large" color="disabled" />,
  open: <OpenIcon fontSize="large" color="warning" />,
  closed: <CheckCircle color="success" fontSize="large" />,
  '': <Cancel fontSize="large" color="primary" />
};

const ViolationStatus = () => {
  const alert = useAlert();
  const { violationData, postId, urlParams } = useContext(ViolationContext);
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user.currentUser);

  const [status, setStatus] = useState<Violations.StatusStage>('');
  const [otherText, setOtherText] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const { mutate: updateStatus, isPending } = useUpdateViolationStatus({
    onSuccess: () => {
      queryClient.setQueryData(
        ['violation', postId, urlParams],
        (oldData: Violations.Details) => ({
          ...oldData,
          status: {
            ...oldData.status,
            stage: status,
            ...(status === 'other' && { comment: otherText }),
            author: user.name,
            updatedAt: new Date().toISOString()
          },
          timeline: [
            ...oldData.timeline,
            {
              type: 'status',
              author: user.name,
              content: status,
              createdAt: new Date().toISOString()
            }
          ]
        })
      );
      alert.success('The status was successfully updated');
      setIsEditing(false);
    },
    onError: () => {
      alert.error('Some error occurred while updating the status');
    }
  });

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as Violations.StatusStage);
    if (event.target.value !== 'other') {
      setOtherText('');
    }
  };

  const handleStatusConfirm = () => {
    updateStatus({
      postId,
      data: {
        status,
        ...(status === 'other' && { comment: otherText })
      }
    });
  };

  const handleCancel = () => {
    setStatus(violationData?.status.stage || '');
    setOtherText(violationData?.status.comment || '');
    setIsEditing(false);
  };

  useEffect(() => {
    setStatus(violationData?.status.stage || '');
    setOtherText(violationData?.status.comment || '');
  }, [violationData]);

  const isConfirmDisabled = useMemo(
    () =>
      status === violationData?.status.stage &&
      otherText === violationData?.status.comment,
    [
      status,
      otherText,
      violationData?.status.stage,
      violationData?.status.comment
    ]
  );

  if (!violationData)
    return <Typography variant="body2">Data not available</Typography>;

  if (violationData?.isExpired) return <></>;
  const statusSubmit = otherText.trim() && !isPending;

  return (
    <Card
      sx={{ marginBottom: 2 }}
      title="Status"
      content={
        <Stack gap={2}>
          {!status || isEditing ? (
            <Stack gap={2}>
              <Typography variant="body2">
                Define the status of this post.
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={status}
                  IconComponent={KeyboardArrowDown}
                  onChange={handleStatusChange}
                  color={selectColor[status as Violations.StatusStage]}
                >
                  {statusOption.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack gap={1} direction="row" alignItems="center">
                        <Circle
                          color={
                            option.color as 'success' | 'warning' | 'disabled'
                          }
                          fontSize="inherit"
                        />
                        <Typography>{option.label}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {status === 'other' && (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Other..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (statusSubmit) handleStatusConfirm();
                    }
                  }}
                />
              )}
            </Stack>
          ) : (
            <>
              <Alert
                variant="outlined"
                sx={{
                  color: 'text.primary',
                  border: '2px solid #dadada'
                }}
                title={`${status.charAt(0).toUpperCase() + status.slice(1)}${
                  status === 'other' && otherText ? ` - ${otherText}` : ''
                }`}
                icon={alertIcon[status as keyof typeof alertIcon]}
                content={
                  violationData?.status.author && (
                    <Typography variant="body2" color="textSecondary">{`${
                      violationData.status.author
                    }, ${getPrettyDateWithTime(
                      violationData.status.updatedAt || ''
                    )}`}</Typography>
                  )
                }
              />
            </>
          )}
        </Stack>
      }
      actions={
        <>
          {isEditing ? (
            <>
              <Button
                size="small"
                variant="text"
                text="cancel"
                onClick={handleCancel}
              />
              <Button
                size="small"
                text="confirm"
                loading={isPending}
                onClick={handleStatusConfirm}
                disabled={isConfirmDisabled}
              />
            </>
          ) : (
            <Button
              size="small"
              text="edit"
              onClick={() => setIsEditing(true)}
            />
          )}
        </>
      }
    />
  );
};

export default ViolationStatus;
